<template>
  <v-fade-transition>
    <div v-if="!!invitationCode" class="container">
      <v-card class="mb-2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div
                class="text-h3 pb-4 text-primary"
                v-text="$t('blocks.invitationCollect.header', { appName })"
              />
              <p
                class="text-subtitle-1"
                v-text="$t('blocks.invitationCollect.desc1')"
              />
              <p
                class="mt-4 text-body-2 opacity-60"
                v-text="$t('blocks.invitationCollect.desc2')"
              />
            </v-col>

            <v-col cols="12" md="6" class="text-center">
              <div class="d-flex flex-column align-center">
                <p
                  class="text-h4"
                  v-text="
                    isRevoking
                      ? $t('blocks.invitationCollect.revokeReward')
                      : $t('blocks.invitationCollect.claimReward')
                  "
                />
                <LivePriceFormatter
                  :ae-price="invitationAmount"
                  :watch-price="false"
                  :price-loading="loadingInvitation"
                  class="text-h2 text-primary mt-2"
                  row
                />
              </div>

              <i18n-t
                v-if="invitationSender && !isRevoking"
                keypath="blocks.invitationCollect.addressInvited"
                class="mt-4 d-flex ga-1 align-center justify-center flex-wrap"
                tag="div"
              >
                <template #address>
                  <AddressChip :address="invitationSender" />
                </template>
                <template #appName>{{ appName }}</template>
              </i18n-t>

              <div
                v-if="errorMessage || successMessage"
                class="text-left mx-auto mt-4"
                style="max-width: 500px"
              >
                <MessageBox
                  v-if="errorMessage"
                  :title="errorMessage"
                  color="error"
                />
                <MessageBox
                  v-else-if="successMessage"
                  :title="$t('common.success')"
                  :text="successMessage"
                  color="success"
                />
              </div>

              <div
                v-if="!loadingInvitation"
                class="d-flex align-center justify-center ga-4 mt-4"
              >
                <v-btn
                  v-if="activeAccount && !invitationClaimed"
                  size="large"
                  color="primary"
                  :loading="claimingReward"
                  :disabled="errorMessage"
                  @click="claimOrRevokeReward()"
                >
                  {{
                    isRevoking
                      ? $t("blocks.invitationCollect.ctaRevoke")
                      : $t("blocks.invitationCollect.ctaClaim")
                  }}
                </v-btn>

                <WalletConnectBtn
                  :label="$t('blocks.invitationCollect.connectWallet')"
                  size="large"
                />

                <v-btn
                  v-if="activeAccount"
                  v-text="$t('common.dismiss')"
                  size="large"
                  color="primary"
                  variant="outlined"
                  @click="close()"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>
  </v-fade-transition>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { AeSdk, CompilerHttp, MemoryAccount } from "@aeternity/aepp-sdk";

import { SETTINGS } from "@/utils/constants";
import { Decimal } from "@/libs/decimal";
import { useAccounts } from "@/stores";
import {
  useAeppSdk,
  useInvitation,
  useNetwork,
  useCommunityFactory,
} from "@/composables";

import WalletConnectBtn from "@/components/WalletConnect/WalletConnectBtn.vue";
import AddressChip from "@/components/Common/AddressChip.vue";
import LivePriceFormatter from "@/components/Common/Pricing/LivePriceFormatter.vue";
import MessageBox from "@/components/Common/MessageBox.vue";

const appName = SETTINGS.app.name;

const router = useRouter();
const route = useRoute();

const { getAeSdk, nodes } = useAeppSdk();
const { activeNetwork } = useNetwork();
const { activeAccount } = storeToRefs(useAccounts());
const { getAffiliationTreasury } = useCommunityFactory();
const { invitationCode } = useInvitation();

const invitationAmount = ref<Decimal>(Decimal.ZERO);
const invitationSender = ref();
const invitationClaimed = ref(false);

const loadingInvitation = ref(false);
const claimingReward = ref(false);

const errorMessage = ref();
const successMessage = ref();

const isRevoking = computed(
  () => invitationSender.value && invitationSender.value === activeAccount.value
);

watch(
  invitationCode,
  (invCode) => {
    if (invCode) {
      getInvitationRewardAmount();
    }
  },
  {
    immediate: true,
  }
);

watch(activeNetwork, () => {
  if (invitationCode.value) {
    getInvitationRewardAmount();
  }
});

/**
 * Retrieves the invitation reward amount for a given invitation code.
 * This function sets the loading state, initializes the AeSdk, retrieves the affiliation contract,
 * and updates the invitation details if the invitation code exists.
 * If the invitation has already been claimed, it displays an error message and closes the component after 3 seconds.
 * @throws {Error} If there is an error retrieving the invitation reward amount.
 */
async function getInvitationRewardAmount() {
  errorMessage.value = null;
  loadingInvitation.value = true;

  try {
    const account = new MemoryAccount(invitationCode.value);
    const sdk = new AeSdk({
      onCompiler: new CompilerHttp("https://v7.compiler.aepps.com"),
      nodes,
      accounts: [account],
    });
    sdk.selectNode(activeNetwork.value.name);

    const affiliationTreasury = await getAffiliationTreasury(sdk);

    const invitationCodes = await affiliationTreasury
      .invitationCodes()
      .catch(() => ({}));

    const invitation = invitationCodes.get(account.address);
    if (invitation) {
      invitationSender.value = invitation[0];
      invitationAmount.value = Decimal.fromBigNumberString(invitation[1]);
      invitationClaimed.value = invitation[2];

      if (invitationClaimed.value) {
        errorMessage.value = "This invitation has already been claimed.";
      }
    }
  } catch (error: any) {
    if (error?.message?.includes("Trying to call undefined function")) {
      errorMessage.value = "Please switch to the correct network.";
    } else {
      errorMessage.value = error?.message;
    }
  }
  loadingInvitation.value = false;
}

async function claimOrRevokeReward() {
  if (!invitationCode.value || !activeAccount.value) {
    return false;
  }
  claimingReward.value = true;
  const sdk = await getAeSdk();
  const affiliationTreasury = await getAffiliationTreasury(sdk);

  try {
    if (isRevoking.value) {
      const account = new MemoryAccount(invitationCode.value);
      await affiliationTreasury.revokeInvitationCode(account.address);
      successMessage.value = "Reward revoked successfully.";
    } else {
      await affiliationTreasury.redeemInvitationCode(
        invitationCode.value,
        activeAccount.value
      );
      successMessage.value = "Reward claimed successfully.";
    }
    invitationClaimed.value = true;
  } catch (error: any) {
    if (error?.message?.includes("INVITEE_ALREADY_REGISTERED")) {
      errorMessage.value =
        "Your account has already claimed an invite reward, try with another account.";
    } else {
      errorMessage.value = `Failed to claim reward. Reason: ${
        error?.message || "Unknown"
      }`;
    }
  }
  claimingReward.value = false;
}

function close() {
  localStorage.removeItem("invite_code");
  router.push({ query: { invite_code: null } });
  invitationCode.value = undefined;
  successMessage.value = null;
}

// Reset and hide the box if user claimed the reward and wants to navigate
watch(
  route,
  () => {
    if (successMessage.value) {
      invitationCode.value = undefined;
      successMessage.value = null;
    }
  },
  { deep: true }
);
</script>
